/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DEFAULT_PASSWORD_REGEXP } from '@app/app/shared/constants/constants';
import { getClientId } from '@app/app/shared/helpers/development-domains.helper';
import { Organization } from '@app/app/shared/models';
import { environment } from '@app/environments/environment';
import { EnvironmentCode } from '@app/environments/environment.model';
import { TranslocoService } from '@ngneat/transloco';
import { Select, Store } from '@ngxs/store';
import { ReCaptchaV3Service, RecaptchaComponent } from 'ng-recaptcha-2';
import { Observable } from 'rxjs';
import { filter, takeWhile } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { AUTH_SERVICE, BasicAuthService } from '../../../services/basic-auth.service';
import { TriggerUserAccountCreation } from '../../../store/user-auth.actions';
import { UserAuthState } from '../../../store/user-auth.state';

@Component({
  selector: 'ptl-signup-details-form',
  templateUrl: './signup-details-form.component.html',
  styleUrls: ['./signup-details-form.component.scss'],
})
export class SignupDetailsFormComponent implements OnInit, OnDestroy {
  @Input() organizationData: Organization;
  @Input() invitationId: string;
  @Input() invitationToken: string;

  /** Emits on back icon click. */
  @Output() backClicked = new EventEmitter<void>();

  @Select(UserAuthState.isAccountCreationInProgress)
  accountCreationInProgress$: Observable<boolean>;

  @Select(UserAuthState.accountCreationError)
  accountCreationError$: Observable<string>;

  @ViewChild('captchaElem', { static: false }) captchaElem: RecaptchaComponent;

  signupDetailsFormGroup: FormGroup;
  hasAffiliation = false;
  componentAlive = true;
  formSubmitted = false;
  showPasswordSelected = false;
  passwordFocusState = false;
  reCaptchaSiteKeyV2 = environment.reCaptchaSiteKeyV2;

  affiliations = [
    { title: 'Student', key: 'STUDENTS' },
    { title: 'Staff', key: 'STAFF' },
  ];

  constructor(
    private store: Store,
    private fb: FormBuilder,
    @Inject(AUTH_SERVICE) private authService: BasicAuthService,
    private captchaV3Service: ReCaptchaV3Service,
    private cdr: ChangeDetectorRef,
    private translocoService: TranslocoService,
  ) {
    store
      .select((state) => state.userAuth.accountCreationState.accountCreationSucceeded)
      .pipe(
        filter(Boolean),
        takeWhile(() => this.componentAlive),
      )
      .subscribe();
  }

  ngOnInit() {
    this.signupDetailsFormGroup = this.fb.group({
      email: ['', Validators.compose([Validators.required, Validators.email])],
      password: ['', Validators.compose([Validators.required, Validators.pattern(DEFAULT_PASSWORD_REGEXP)])],
      termsOfService: [''],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
    });
    if (this.invitationId) {
      this.authService.validateInvitationToken(this.invitationId, this.invitationToken).subscribe(({ isSuccess, value }) => {
        if (!isSuccess) {
          this.onBackClicked();
        }
        this.signupDetailsFormGroup.controls['email'].setValue(value.email);

        if (this.organizationData?.type === 'EDUCATIONAL' && value.affiliationSelectionEnabled) {
          this.showAffiliationField();
        }
      });
    } else {
      if (this.organizationData?.type === 'EDUCATIONAL') {
        this.showAffiliationField();
      }
    }
  }

  ngOnDestroy() {
    this.componentAlive = false;
  }

  openWhitelistDomains(event: MouseEvent) {
    event.preventDefault();
    const target = event.target as HTMLElement;
    if (target.closest('a') !== null) {
      this.openWhitelistDomainsPopup();
    }
  }

  onSignUpClick() {
    this.captchaElem.execute();
  }

  onBackClicked() {
    this.backClicked.next();
  }

  signInUsingLinkedIn() {
    this.redirectToIdpViaRealm('linkedin');
  }

  signInUsingGoogle() {
    this.redirectToIdpViaRealm('google');
  }

  async proceedIfValid(captchaResponseV2: string | null) {
    if (!captchaResponseV2) return;
    await this.submitForm(captchaResponseV2);
  }

  private showAffiliationField() {
    this.hasAffiliation = true;
    this.signupDetailsFormGroup.addControl('affiliation', new FormControl('', Validators.required));
  }

  private openWhitelistDomainsPopup() {
    let domainsList = '';
    for (const domain of this.organizationData.whitelist) {
      domainsList += `<li>${domain}</li>`;
    }
    const contentHtml = '<ul>' + domainsList + '</ul>';
    const title = this.translocoService.translate('translations.whitelist.description.registerPermittedEmailsDescription');
    Swal.fire({
      title: title,
      position: this.organizationData.landingPage.layout === 'SPLIT_SCREEN' ? 'center-left' : 'center',
      html: contentHtml,
      customClass: { popup: 'whitelist-domains-popup' },
      showCancelButton: false,
      showConfirmButton: true,
      confirmButtonText: this.translocoService.translate('translations.global.button.ok'),
    });
  }

  private redirectToIdpViaRealm(idp: string) {
    document.location.href =
      `${environment.realmUrl}protocol/openid-connect/auth?` +
      `client_id=${getClientId(this.organizationData?.domain)}&` +
      'response_type=token&' +
      `nonce=${environment.nonce}&` +
      `kc_idp_hint=${idp}&` +
      `redirect_uri=${this.getRedirectUri()}`;
  }

  private getRedirectUri() {
    if ([EnvironmentCode.dev, EnvironmentCode.prod].includes(environment.env) && window.location.hostname.includes('localhost')) {
      return 'http://localhost:4200';
    }

    return `https://${this.organizationData?.domain}`;
  }

  private async submitForm(captchaResponseV2: string) {
    this.formSubmitted = true;
    if (this.signupDetailsFormGroup.valid) {
      const captchaActionName = this.invitationId && this.invitationToken ? 'invitationUserRegistration' : 'userRegistration';
      this.captchaV3Service.execute(captchaActionName).subscribe((captchaResponseV3: string) => {
        const request = {
          email: this.signupDetailsFormGroup.get('email').value?.trim(),
          password: this.signupDetailsFormGroup.get('password').value?.trim(),
          firstName: this.signupDetailsFormGroup.get('firstName').value?.trim(),
          lastName: this.signupDetailsFormGroup.get('lastName').value?.trim(),
          captchaResponseV2: captchaResponseV2,
          captchaResponseV3: captchaResponseV3,
        };
        if (this.hasAffiliation) {
          const affiliation = this.signupDetailsFormGroup.get('affiliation').value;
          request['targetGroups'] = [affiliation];
        }
        this.store.dispatch(new TriggerUserAccountCreation(request, this.invitationId, this.invitationToken));
        this.captchaElem.reset();
        this.cdr.detectChanges();
      });
    } else {
      this.signupDetailsFormGroup.get('termsOfService').markAsDirty();
    }
  }
}
